import React, { useState, useEffect } from 'react';
import { X, Search, Save } from 'lucide-react';
import { accessControlAPI, userAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface UserAccessControl {
  id: number;
  user_id?: number;
  email: string;
  first_name: string;
  last_name: string;
  data_discovery_access: 'Y' | 'N' | 'C' | null;
  license_access: 'Y' | 'N' | 'C' | null;
}

interface ManageAccessControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ManageAccessControlModal: React.FC<ManageAccessControlModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [users, setUsers] = useState<UserAccessControl[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserAccessControl[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = users.filter(
        (u) =>
          u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await accessControlAPI.getUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAccess = async (userId: number, field: 'data_discovery_access' | 'license_access', value: 'Y' | 'N' | 'C') => {
    try {
      setIsSaving(userId);
      const user = users.find((u) => (u.user_id || u.id) === userId);
      if (!user) return;

      await accessControlAPI.updateUserAccess(userId, {
        data_discovery_access: field === 'data_discovery_access' ? value : user.data_discovery_access || 'N',
        license_access: field === 'license_access' ? value : user.license_access || 'N',
      });

      // Update local state
      const userIdKey = (u: UserAccessControl) => u.user_id || u.id;
      setUsers((prev) =>
        prev.map((u) =>
          userIdKey(u) === userId
            ? {
                ...u,
                [field]: value,
              }
            : u
        )
      );
      setFilteredUsers((prev) =>
        prev.map((u) =>
          userIdKey(u) === userId
            ? {
                ...u,
                [field]: value,
              }
            : u
        )
      );

      toast.success('Access updated successfully');
      onSuccess();
    } catch (error: any) {
      console.error('Error updating access:', error);
      toast.error(error.response?.data?.error || 'Failed to update access');
    } finally {
      setIsSaving(null);
    }
  };

  const getAccessBadgeColor = (access: 'Y' | 'N' | 'C' | null) => {
    switch (access) {
      case 'Y':
        return 'bg-green-100 text-green-800';
      case 'C':
        return 'bg-purple-100 text-purple-800';
      case 'N':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLabel = (access: 'Y' | 'N' | 'C' | null) => {
    switch (access) {
      case 'Y':
        return 'Yes';
      case 'C':
        return 'Control';
      case 'N':
      default:
        return 'No';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage User Access Control</h2>
            <p className="text-sm text-gray-600 mt-1">Control who can access Data Discovery and Licenses</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
          </div>

          {/* Legend */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Access Levels:</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium mr-2">Yes</span>
                  <span>User can access</span>
                </div>
                <div>
                  <span className="inline-block px-2 py-1 rounded bg-gray-100 text-gray-800 text-xs font-medium mr-2">No</span>
                  <span>User cannot access</span>
                </div>
                <div>
                  <span className="inline-block px-2 py-1 rounded bg-purple-100 text-purple-800 text-xs font-medium mr-2">Control</span>
                  <span>User can manage access</span>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Discovery</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Licenses</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => {
                    const userId = user.user_id || user.id;
                    return (
                      <tr key={userId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={user.data_discovery_access || 'N'}
                            onChange={(e) =>
                              handleUpdateAccess(userId, 'data_discovery_access', e.target.value as 'Y' | 'N' | 'C')
                            }
                            disabled={isSaving === userId}
                            className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] disabled:opacity-50"
                          >
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                            <option value="C">Control</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={user.license_access || 'N'}
                            onChange={(e) =>
                              handleUpdateAccess(userId, 'license_access', e.target.value as 'Y' | 'N' | 'C')
                            }
                            disabled={isSaving === userId}
                            className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] disabled:opacity-50"
                          >
                            <option value="N">No</option>
                            <option value="Y">Yes</option>
                            <option value="C">Control</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageAccessControlModal;
